import { PlusOutlined } from "@ant-design/icons";
import { Button, Divider, message } from "antd";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppContext from "../../context/app/context";
import { useIsMount } from "../../utils/hooks/is-mount";
import styles from "./style.module.scss";
import PageHeader from "../../components/general/layout/page-header";
import RefreshBtn from "../../components/general/table-components/actions/refresh-btn";
import { Permissions } from "../../utils/Permissions/Permissions";
import ReportContextProvider from "../../context/report/provider";
import ReportContext from "../../context/report/context";
import ReportList from "../../components/report/report-list";
import SearchFilterCard from "../../components/general/filter-card";
import FormHookModal from "../../components/general/modals/form-hook-modal";
import { useModal } from "react-modal-hook";
import ReportForm from "../../components/report/report-form";
import ReportFilter from "../../components/report/report-filter";
import { ReportType } from "../../models/report/enum";
import { ICreateReport } from "../../models/report/request";
import AuthContext from "../../context/auth/context";

interface IProps {
  children?: React.ReactNode;
}

const buildProductBatchStockReportRequest = (
  data: any,
  t: (key: string) => string,
): ICreateReport | null => {
  const body: ICreateReport = {};
  if (data.store_id) {
    body.store_id = data.store_id;
  }
  const mode = data.batchStockFilterMode ?? "expiration";
  if (mode === "remaining") {
    if (
      data.remaining_days === undefined ||
      data.remaining_days === null ||
      data.remaining_days === ""
    ) {
      message.warning(t("remaining_days_required"));
      return null;
    }
    body.remaining_days = Number(data.remaining_days);
    return body;
  }
  if (data.startDate) {
    body.startDate = data.startDate;
  }
  if (data.endDate) {
    body.endDate = data.endDate;
  }
  if (
    body.startDate &&
    body.endDate &&
    moment(body.endDate).isBefore(moment(body.startDate), "day")
  ) {
    message.warning(t("product_batch_stock_end_before_start"));
    return null;
  }
  return body;
};

const buildCouponImpactReportRequest = (
  data: any,
  t: (key: string) => string,
): ICreateReport | null => {
  const mode = data.couponSelectMode ?? "select";

  if (mode === "manual") {
    const code = (data.couponCode ?? "").trim();
    if (!code) {
      message.warning(t("coupon_required"));
      return null;
    }
    return { code };
  }

  if (!data.discountCodeId) {
    message.warning(t("coupon_required"));
    return null;
  }
  return { id: Number(data.discountCodeId) };
};

const buildLowStockReportRequest = (data: any): ICreateReport => {
  const request: ICreateReport = {
    store_id: data.store_id,
    threshold: data.threshold,
  };

  if (data.startDate) {
    request.startDate = data.startDate;
  }

  if (data.startDate && data.endDate) {
    request.endDate = data.endDate;
  }

  return request;
};

const hasReportValue = (value: any) =>
  value !== undefined && value !== null && value !== "";

const getNumberArray = (value: any) =>
  Array.isArray(value)
    ? value.map(Number).filter((item) => Number.isFinite(item))
    : [];

const buildRfmReportRequest = (data: any): ICreateReport => {
  const request: ICreateReport = {
    startDate: data.startDate,
    endDate: data.endDate,
  };

  if (hasReportValue(data.historyStartDate)) {
    request.historyStartDate = data.historyStartDate;
  }
  if (hasReportValue(data.historyEndDate)) {
    request.historyEndDate = data.historyEndDate;
  }
  if (Array.isArray(data.rScores) && data.rScores.length > 0) {
    request.rScores = data.rScores.map(Number);
  }
  if (Array.isArray(data.fScores) && data.fScores.length > 0) {
    request.fScores = data.fScores.map(Number);
  }
  if (Array.isArray(data.mScores) && data.mScores.length > 0) {
    request.mScores = data.mScores.map(Number);
  }
  if (hasReportValue(data.recencyDaysMin)) {
    request.recencyDaysMin = Number(data.recencyDaysMin);
  }
  if (hasReportValue(data.recencyDaysMax)) {
    request.recencyDaysMax = Number(data.recencyDaysMax);
  }
  if (hasReportValue(data.frequencyMin)) {
    request.frequencyMin = Number(data.frequencyMin);
  }
  if (hasReportValue(data.frequencyMax)) {
    request.frequencyMax = Number(data.frequencyMax);
  }
  if (hasReportValue(data.monetaryMin)) {
    request.monetaryMin = Number(data.monetaryMin);
  }
  if (hasReportValue(data.monetaryMax)) {
    request.monetaryMax = Number(data.monetaryMax);
  }
  if (
    Array.isArray(data.activityStatuses) &&
    data.activityStatuses.length > 0
  ) {
    request.activityStatuses = data.activityStatuses;
  }
  if (Array.isArray(data.storeIds) && data.storeIds.length > 0) {
    request.storeIds = data.storeIds.map(Number);
    request.storeFilterMode =
      data.storeFilterMode ?? "dominant_store_by_orders";
  }

  return request;
};

const buildCustomerSatisfactionReportRequest = (data: any): ICreateReport => {
  const request: ICreateReport = {};

  if (data.startDate) request.startDate = data.startDate;
  if (data.endDate) request.endDate = data.endDate;
  if (Array.isArray(data.storeIds) && data.storeIds.length > 0) {
    request.storeIds = data.storeIds.map(Number);
  }
  if (Array.isArray(data.areaIds) && data.areaIds.length > 0) {
    request.areaIds = data.areaIds.map(Number);
  }
  if (
    Array.isArray(data.satisfactionStatuses) &&
    data.satisfactionStatuses.length > 0
  ) {
    request.satisfactionStatuses = data.satisfactionStatuses.map(Number);
  }
  if (Array.isArray(data.reasonIds) && data.reasonIds.length > 0) {
    request.reasonIds = data.reasonIds.map(Number);
  }
  if (Array.isArray(data.driverIds) && data.driverIds.length > 0) {
    request.driverIds = data.driverIds.map(Number);
  }
  if (hasReportValue(data.hasDiscountCode)) {
    request.hasDiscountCode = data.hasDiscountCode;
  }
  if (
    data.hasDiscountCode !== false &&
    Array.isArray(data.discountCodeIds) &&
    data.discountCodeIds.length > 0
  ) {
    request.discountCodeIds = data.discountCodeIds.map(Number);
  }
  if (hasReportValue(data.hasFreeDelivery)) {
    request.hasFreeDelivery = data.hasFreeDelivery;
  }
  if (hasReportValue(data.fulfillmentStatus)) {
    request.fulfillmentStatus = data.fulfillmentStatus;
  }

  return request;
};


const buildCustomerGeographicDistributionReportRequest = (
  data: any,
): ICreateReport => {
  const request: ICreateReport = {};

  if (data.clientCreatedStartDate) {
    request.clientCreatedStartDate = data.clientCreatedStartDate;
  }
  if (data.clientCreatedEndDate) {
    request.clientCreatedEndDate = data.clientCreatedEndDate;
  }
  if (data.orderStartDate) {
    request.orderStartDate = data.orderStartDate;
  }
  if (data.orderEndDate) {
    request.orderEndDate = data.orderEndDate;
  }

  const coverageAreaIds = getNumberArray(data.coverageAreaIds);
  const includeOutsideCoverageAreas =
    data.includeOutsideCoverageAreas === true;

  if (coverageAreaIds.length > 0) {
    request.coverageAreaIds = coverageAreaIds;
  }
  request.coverageAreaSource = data.coverageAreaSource ?? "any";
  request.includeOutsideCoverageAreas = includeOutsideCoverageAreas;

  const discountAreaIds = getNumberArray(data.discountAreaIds);
  const includeOutsideDiscountAreas = data.includeOutsideDiscountAreas === true;

  if (discountAreaIds.length > 0) {
    request.discountAreaIds = discountAreaIds;
  }
  request.discountAreaSource = data.discountAreaSource ?? "any";
  request.includeOutsideDiscountAreas = includeOutsideDiscountAreas;

  return request;
};

const buildSavedReportRequest = (data: any): ICreateReport => {
  switch (data.type) {
    case ReportType.RFM_REPORT:
      return buildRfmReportRequest(data);

    case ReportType.DRIVER_PAYMENTS: {
      const request: ICreateReport = {};

      if (data.startDate) request.startDate = data.startDate;
      if (data.endDate) request.endDate = data.endDate;
      if (data.driverId) request.driverId = data.driverId;

      return request;
    }

    case ReportType.USER_ACTIVITY_REPORT: {
      const request: ICreateReport = {};

      if (data.startDate) request.startDate = data.startDate;
      if (data.endDate) request.endDate = data.endDate;
      if (data.min_orders !== undefined && data.min_orders !== "")
        request.min_orders = data.min_orders;
      if (data.max_orders !== undefined && data.max_orders !== "")
        request.max_orders = data.max_orders;

      return request;
    }

    case ReportType.MONTHLY_USER_ACTIVITY_REPORT: {
      const request: ICreateReport = {};

      if (data.startDate) request.startDate = data.startDate;
      if (data.endDate) request.endDate = data.endDate;
      if (
        data.min_orders_per_month !== undefined &&
        data.min_orders_per_month !== ""
      ) {
        request.min_orders_per_month = data.min_orders_per_month;
      }

      return request;
    }

    case ReportType.CART_BEHAVIOR_REPORT: {
      const request: ICreateReport = {};

      if (data.startDate) request.startDate = data.startDate;
      if (data.endDate) request.endDate = data.endDate;
      if (hasReportValue(data.cartValueMin)) {
        request.cartValueMin = Number(data.cartValueMin);
      }
      if (hasReportValue(data.cartValueMax)) {
        request.cartValueMax = Number(data.cartValueMax);
      }
      if (hasReportValue(data.minOrdersPerUser)) {
        request.minOrdersPerUser = Number(data.minOrdersPerUser);
      }
      if (hasReportValue(data.maxOrdersPerUser)) {
        request.maxOrdersPerUser = Number(data.maxOrdersPerUser);
      }
      if (hasReportValue(data.completionMinutesMin)) {
        request.completionMinutesMin = Number(data.completionMinutesMin);
      }
      if (hasReportValue(data.completionMinutesMax)) {
        request.completionMinutesMax = Number(data.completionMinutesMax);
      }
      if (
        Array.isArray(data.deliveryTypes) &&
        data.deliveryTypes.length === 1
      ) {
        request.deliveryTypes = data.deliveryTypes;
      }
      if (Array.isArray(data.storeIds) && data.storeIds.length > 0) {
        request.storeIds = data.storeIds.map(Number);
      }
      if (Array.isArray(data.categoryIds) && data.categoryIds.length > 0) {
        request.categoryIds = data.categoryIds.map(Number);
      }
      if (Array.isArray(data.daypartKeys) && data.daypartKeys.length > 0) {
        request.daypartKeys = data.daypartKeys;
      }

      return request;
    }

    case ReportType.CUSTOMER_SATISFACTION_REPORT:
      return buildCustomerSatisfactionReportRequest(data);

    case ReportType.CUSTOMER_GEOGRAPHIC_DISTRIBUTION_REPORT:
      return buildCustomerGeographicDistributionReportRequest(data);

    case ReportType.CATEGORY_SALES_TIMING_REPORT: {
      const request: ICreateReport = {};

      if (data.startDate) request.startDate = data.startDate;
      if (data.endDate) request.endDate = data.endDate;
      if (Array.isArray(data.storeIds) && data.storeIds.length > 0) {
        request.storeIds = data.storeIds.map(Number);
      }
      if (Array.isArray(data.categoryIds) && data.categoryIds.length > 0) {
        request.categoryIds = data.categoryIds.map(Number);
      }
      if (Array.isArray(data.daypartKeys) && data.daypartKeys.length > 0) {
        request.daypartKeys = data.daypartKeys;
      }

      return request;
    }

    case ReportType.PRODUCT_MARGIN_REPORT: {
      const request: ICreateReport = {};

      if (data.store_id) request.store_id = data.store_id;
      if (data.trademark_id) request.trademark_id = data.trademark_id;
      if (data.product_status) request.product_status = data.product_status;
      if (data.store_quantity_status) {
        request.store_quantity_status = data.store_quantity_status;
      }

      return request;
    }

    default: {
      const request: ICreateReport = {};

      if (data.startDate) request.startDate = data.startDate;
      if (data.endDate) request.endDate = data.endDate;

      return request;
    }
  }
};

const Report: React.FC<IProps> = () => {
  const { t } = useTranslation();

  const { direction } = useContext(AppContext);
  const { userPermissions: permissions } = useContext(AuthContext);

  const [search, setSearch] = useState<string | undefined>(undefined);

  const { actions, loading, query } = useContext(ReportContext);
  let userPermissions =
    permissions?.length > 0
      ? permissions
      : localStorage?.getItem("permisionStoroge")?.split(",");
  if (!userPermissions) userPermissions = [];

  const hasPermission = (permission: string) =>
    userPermissions.includes(permission);
  const canExportProductMarginReport =
    hasPermission(Permissions.CAN_EXPORT_PRODUCT_MARGIN_REPORT) &&
    hasPermission(Permissions.CAN_MANAGE_PURCHASE_PRICE);
  const canExportAnyReport =
    canExportProductMarginReport ||
    [
      Permissions.CAN_EXPORT_PHARMACIES_ORDERS_REPORT,
      Permissions.CAN_EXPORT_ORDER_USER_DETAILS_REPORT,
      Permissions.CAN_EXPORT_ORDER_CANCELLATION_REPORT,
      Permissions.CAN_EXPORT_DRIVER_PERFORMANCE_REPORT,
      Permissions.CAN_EXPORT_ORDERS_DAYLE_INFO_REPORT,
      Permissions.CAN_EXPORT_ORDER_STATUS_REPORT,
      Permissions.CAN_EXPORT_PAID_ORDERS_JOURNEY_REPORT,
      Permissions.CAN_EXPORT_ORDERS_DETAILS_REPORT,
      Permissions.CAN_EXPORT_USER_ACTIVITY_REPORT,
      Permissions.CAN_EXPORT_MONTHLY_USER_ACTIVITY_REPORT,
      Permissions.CAN_EXPORT_LOW_STOCK_REPORT,
      Permissions.CAN_EXPORT_PRODUCT_BATCH_STOCK_REPORT,
      Permissions.CAN_EXPORT_SECTOR_DELIVERY_TIME_REPORT,
      Permissions.CAN_EXPORT_STORE_ACCEPTANCE_TIME_REPORT,
      Permissions.CAN_EXPORT_PREPARING_TIME_REPORT,
      Permissions.CAN_EXPORT_ORDER_CYCLE_TIME_REPORT,
      Permissions.CAN_EXPORT_MODIFIED_ORDER_ACCEPTANCE_TIME_REPORT,
      Permissions.CAN_EXPORT_ACCOUNTING_FINALIZATION_TIME_REPORT,
      Permissions.CAN_EXPORT_DRIVER_SEARCH_TIME_REPORT,
      Permissions.CAN_EXPORT_CUSTOMER_CANCELLATION_REPORT,
      Permissions.CAN_EXPORT_ADMINISTRATIVE_CANCELLATION_REPORT,
      Permissions.CAN_EXPORT_CUSTOMER_SATISFACTION_REPORT,
      Permissions.CAN_EXPORT_CUSTOMER_GEOGRAPHIC_DISTRIBUTION_REPORT,
      Permissions.CAN_EXPORT_COUPON_IMPACT_REPORT,
      Permissions.CAN_EXPORT_CART_BEHAVIOR_REPORT,
      Permissions.CAN_EXPORT_CATEGORY_SALES_TIMING_REPORT,
      Permissions.CAN_EXPORT_RFM_REPORT,
    ].some(hasPermission);

  useEffect(() => {
    actions.setSearch(search);
  }, [search]);

  const isMounted = useIsMount();
  useEffect(() => {
    !isMounted && actions.getData();
  }, [query, direction]);

  const [showFormModal, hideForModal] = useModal(
    () => (
      <FormHookModal
        id="4"
        confirmButtonText={t("save")}
        loading={loading.includes("create")}
        onSubmit={async (data) => {
          if (data.type === ReportType.LOW_STOCK_REPORT) {
            await actions.createLowStockReport(
              buildLowStockReportRequest(data),
            );
          } else if (data.type === ReportType.PRODUCT_BATCH_STOCK_REPORT) {
            const payload = buildProductBatchStockReportRequest(data, t);
            if (payload === null) {
              throw new Error("product_batch_stock_validation");
            }
            await actions.createReport(payload, data.type);
          } else if (data.type === ReportType.COUPON_IMPACT) {
            const payload = buildCouponImpactReportRequest(data, t);
            if (payload === null) {
              throw new Error("coupon_impact_validation");
            }
            await actions.createReport(payload, data.type);
          } else {
            await actions.createReport(
              buildSavedReportRequest(data),
              data.type,
            );
          }
        }}
        hideModal={() => {
          hideForModal();
        }}
        width={"50vw"}
        style={{
          marginTop: "1.6rem",
          height: "59vh",
        }}
        title={t("add_report")}
      >
        <ReportForm />
      </FormHookModal>
    ),
    [loading],
  );

  return (
    <div dir={direction} className={styles.container}>
      <PageHeader
        title={t("reports")}
        subTitle={t("reports_management")}
        extra={[
          <RefreshBtn
            key={0}
            loading={loading.includes("list")}
            onClick={() => actions.getData()}
          >
            {t("refresh")}
          </RefreshBtn>,
          <Divider key={1} type="vertical" />,
          canExportAnyReport ? (
            <Button
              key={2}
              type="primary"
              onClick={() => {
                showFormModal();
              }}
              icon={<PlusOutlined />}
            >
              {t("add_report")}
            </Button>
          ) : null,
        ]}
      />
      <SearchFilterCard
        formId="report-filter"
        searchValue={search}
        setSearchValue={setSearch}
        onReset={() => {
          actions.setQuery({
            page: query?.page,
            perPage: query?.perPage,
          });
        }}
        resetLoading={loading.includes("list")}
        applyLoading={loading.includes("list")}
      >
        <ReportFilter></ReportFilter>
      </SearchFilterCard>

      <ReportList></ReportList>
    </div>
  );
};

const ReportPage = () => {
  return (
    // ContextProvider
    <ReportContextProvider>
      <Report />
    </ReportContextProvider>
  );
};
export default ReportPage;
